import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material'
import { useSnackbar } from 'notistack'

import { selectMiscItems, useUpdateMiscItemsMutation } from '@/app/services/miscItems'
import { selectQuote, useUpdateQuoteMutation } from '@/app/services/quotes'
import { selectOrganisationId } from '@/app/slices/organisationSlice'
import { getSortFunction } from '@/common/utils/quoteUtilities'

const MiscItemListSort = () => {
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()
    const { quoteId } = useParams()

    const organisationId = useSelector(selectOrganisationId)
    const selectedQuote = useSelector((state) => selectQuote(state, { organisationId: organisationId, quoteId }))
    const miscItems = useSelector((state) => selectMiscItems(state, { organisationId: organisationId, quoteId }))

    const [selectedCriteria, setSelectedCriteria] = useState(selectedQuote.sortMiscItemsBy ?? 'Default')
    const [selectedDirection, setSelectedDirection] = useState(selectedQuote.sortMiscItemsAsc ? 'ASC' : 'DESC')

    const [updateQuote] = useUpdateQuoteMutation()
    const [updateMiscItems] = useUpdateMiscItemsMutation()

    const handleUpdateQuote = async (updatedFields) => {
        //dispatch(setSelectedQuoteStatus(QuoteStatus.NotCalculated))
        try {
            await updateQuote({
                organisationId,
                quoteId,
                quote: {
                    ...selectedQuote,
                    ...updatedFields,
                },
            }).unwrap()
        } catch (_e) {
            const errorMessage = t('$t(An error occurred) updating the $t(quote).')
            enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 5000 })
        }
    }

    const handleUpdateMiscItems = async (sortedList) => {
        try {
            await updateMiscItems({
                organisationId,
                quoteId,
                miscItems: sortedList,
            }).unwrap()
        } catch (e) {
            enqueueSnackbar(t('Failed to reorder $t(quote) items. Undoing changes'), {
                variant: 'error',
                autoHideDuration: 5000,
            })
        }
    }

    const handleCriteriaChange = (event) => {
        const { value } = event.target
        setSelectedCriteria(value)

        const sortedList = [...miscItems]
            .sort(getSortFunction(value, selectedDirection))
            .map((miscItem, index) => ({ ...miscItem, index }))

        handleUpdateQuote({ sortMiscItemsBy: value })
        handleUpdateMiscItems(sortedList)
    }

    const handleDirectionChange = (event) => {
        const { value } = event.target
        setSelectedDirection(value)

        const sortedList = [...miscItems]
            .sort(getSortFunction(selectedCriteria, value))
            .map((miscItem, index) => ({ ...miscItem, index }))

        handleUpdateQuote({ sortMiscItemsAsc: value == 'ASC' })
        handleUpdateMiscItems(sortedList)
    }

    useEffect(() => {
        setSelectedCriteria(selectedQuote.sortMiscItemsBy ?? 'Custom')
        setSelectedDirection(selectedQuote.sortMiscItemsAsc ? 'ASC' : 'DESC')
    }, [selectedQuote])

    return (
        <>
            <FormControl variant="outlined">
                <InputLabel
                    data-testid="sort-misc-by-label"
                    id="selected-misc-criteria-label"
                >
                    {t('Sort by')}
                </InputLabel>
                <Select
                    data-testid="sort-by"
                    id="selected-misc-criteria"
                    label={t('Sort by')}
                    labelId="selected-misc-criteria-label"
                    size="small"
                    value={selectedCriteria}
                    onChange={handleCriteriaChange}
                >
                    <MenuItem value={'Default'}>{t('Upload order')}</MenuItem>
                    {selectedCriteria === 'Custom' ? <MenuItem value={'Custom'}>{t('User defined')}</MenuItem> : null}
                    <MenuItem value={'Quantity'}>{t('Quantity')}</MenuItem>
                    <MenuItem value={'UnitPrice'}>{t('Unit price')}</MenuItem>
                    <MenuItem value={'Name'}>{t('Item name')}</MenuItem>
                </Select>
            </FormControl>
            <FormControl variant="outlined">
                <InputLabel
                    data-testid="order-misc-by-label"
                    id="selected-misc-direction-label"
                >
                    {t('Order by')}
                </InputLabel>
                <Select
                    data-testid="order-by"
                    disabled={selectedCriteria === 'Custom'}
                    id="selected-misc-direction"
                    label={t('Order by')}
                    labelId="selected-misc-direction-label"
                    size="small"
                    value={selectedDirection}
                    onChange={handleDirectionChange}
                >
                    <MenuItem value={'ASC'}>{t('ASC')}</MenuItem>
                    <MenuItem value={'DESC'}>{t('DESC')}</MenuItem>
                </Select>
            </FormControl>
        </>
    )
}

export default MiscItemListSort
